import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  GraduationCap, LogOut, BookOpen, Clock, Trophy, Target, 
  Users, Award, FileText, CheckCircle2, Download,
  Calendar, Activity, Zap, Brain, User, ArrowRight, RefreshCw,
  Edit3, Mail, MapPin, Briefcase, Eye, Save, X, Sparkles,
  ChevronRight, AlertCircle, Star, ClipboardCheck, MessageSquare,
  TrendingUp, FileUp, Check, BarChart3, PieChart, TrendingDown,
  UserCheck, AlertTriangle, Medal, Flame, BookMarked, School,
  ChevronDown, ExternalLink, Search
} from 'lucide-react';
import Header from '../components/Header';
import MessagingPanel from '../components/MessagingPanel';
import AIInteractionInsights from '../components/AIInteractionInsights';
import axios from 'axios';
import { toast } from "sonner";
import { PieChart as RechartsPie, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, Legend, AreaChart, Area } from 'recharts';

export default function TeacherProfile() {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isTeacher, setIsTeacher] = useState(false);
  const [teacherCourses, setTeacherCourses] = useState([]);
  const [students, setStudents] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentProgress, setStudentProgress] = useState(null);
  const [loadingProgress, setLoadingProgress] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedCourse, setSelectedCourse] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedCourse, setExpandedCourse] = useState(null);
  
  // Analytics data
  const [analytics, setAnalytics] = useState({
    totalStudents: 0,
    avgClassPerformance: 0,
    assignmentCompletionRate: 0,
    pendingGrades: 0,
    gradedSubmissions: 0,
    gradeDistribution: [],
    topPerformers: [],
    needsAttention: [],
    courseEngagement: [],
    weeklyActivity: []
  });
  
  // Class performance data (from backend)
  const [classPerformance, setClassPerformance] = useState(null);
  const [loadingClassPerformance, setLoadingClassPerformance] = useState(false);
  
  // Grading state
  const [gradingSubmission, setGradingSubmission] = useState(null);
  const [gradeValue, setGradeValue] = useState('');
  const [feedbackValue, setFeedbackValue] = useState('');
  const [isGrading, setIsGrading] = useState(false);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

  const handleLogout = useCallback(() => {
    localStorage.removeItem('faheem_token');
    localStorage.removeItem('faheem_auth');
    localStorage.removeItem('faheem_user');
    localStorage.removeItem('faheem_role');
    navigate('/login');
  }, [navigate]);

  // Calculate analytics from data
  const calculateAnalytics = useCallback((studentsData, submissionsData, coursesData) => {
    const totalStudents = studentsData.length;
    const gradedSubs = submissionsData.filter(s => s.is_graded);
    const pendingGrades = submissionsData.filter(s => !s.is_graded).length;
    const gradedSubmissions = gradedSubs.length;
    
    // Average class performance
    const avgPerformance = gradedSubs.length > 0 
      ? Math.round(gradedSubs.reduce((acc, s) => acc + (s.grade || 0), 0) / gradedSubs.length)
      : 0;
    
    // Grade distribution
    const gradeRanges = { 'A (90-100)': 0, 'B (80-89)': 0, 'C (70-79)': 0, 'D (60-69)': 0, 'F (<60)': 0 };
    gradedSubs.forEach(s => {
      if (s.grade >= 90) gradeRanges['A (90-100)']++;
      else if (s.grade >= 80) gradeRanges['B (80-89)']++;
      else if (s.grade >= 70) gradeRanges['C (70-79)']++;
      else if (s.grade >= 60) gradeRanges['D (60-69)']++;
      else gradeRanges['F (<60)']++;
    });
    
    const gradeDistribution = Object.entries(gradeRanges).map(([name, value]) => ({ name, value }));
    
    // Top performers (students with highest average grades)
    const studentGrades = {};
    gradedSubs.forEach(s => {
      if (!studentGrades[s.student_id]) {
        studentGrades[s.student_id] = { 
          total: 0, 
          count: 0, 
          name: s.student_name,
          id: s.student_id 
        };
      }
      studentGrades[s.student_id].total += s.grade || 0;
      studentGrades[s.student_id].count++;
    });
    
    const topPerformers = Object.values(studentGrades)
      .map(s => ({ ...s, avg: Math.round(s.total / s.count) }))
      .sort((a, b) => b.avg - a.avg)
      .slice(0, 5);
    
    // Students needing attention (low performance or no submissions)
    const needsAttention = Object.values(studentGrades)
      .map(s => ({ ...s, avg: Math.round(s.total / s.count) }))
      .filter(s => s.avg < 70)
      .sort((a, b) => a.avg - b.avg)
      .slice(0, 5);
    
    // Add students with no submissions
    const studentsWithSubmissions = new Set(Object.keys(studentGrades));
    studentsData.forEach(student => {
      if (!studentsWithSubmissions.has(student.id) && needsAttention.length < 5) {
        needsAttention.push({
          id: student.id,
          name: student.username || student.email,
          avg: 0,
          count: 0,
          noSubmissions: true
        });
      }
    });
    
    // Course engagement
    const courseEngagement = coursesData.map(course => {
      const courseStudents = studentsData.filter(s => 
        s.enrolled_courses && s.enrolled_courses.includes(course.id)
      ).length;
      const courseSubs = submissionsData.filter(s => s.course_id === course.id).length;
      return {
        name: course.title.length > 20 ? course.title.substring(0, 20) + '...' : course.title,
        students: courseStudents,
        submissions: courseSubs
      };
    });
    
    // Weekly activity - calculate from actual submission dates
    const now = new Date();
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const weeklyActivityMap = {};
    
    // Initialize all days of the week
    for (let i = 6; i >= 0; i--) {
      const date = new Date(now);
      date.setDate(date.getDate() - i);
      const dayName = dayNames[date.getDay()];
      weeklyActivityMap[dayName] = { day: dayName, submissions: 0, graded: 0 };
    }
    
    // Count submissions by day
    submissionsData.forEach(sub => {
      if (sub.created_at) {
        const subDate = new Date(sub.created_at);
        const daysDiff = Math.floor((now - subDate) / (1000 * 60 * 60 * 24));
        if (daysDiff >= 0 && daysDiff < 7) {
          const dayName = dayNames[subDate.getDay()];
          if (weeklyActivityMap[dayName]) {
            weeklyActivityMap[dayName].submissions++;
            if (sub.is_graded) {
              weeklyActivityMap[dayName].graded++;
            }
          }
        }
      }
    });
    
    // Convert to array in order (last 7 days)
    const weeklyActivity = [];
    for (let i = 6; i >= 0; i--) {
      const date = new Date(now);
      date.setDate(date.getDate() - i);
      const dayName = dayNames[date.getDay()];
      weeklyActivity.push(weeklyActivityMap[dayName]);
    }
    
    setAnalytics({
      totalStudents,
      avgClassPerformance: avgPerformance,
      assignmentCompletionRate: totalStudents > 0 ? Math.round((gradedSubmissions / Math.max(totalStudents, 1)) * 100) : 0,
      pendingGrades,
      gradedSubmissions,
      gradeDistribution,
      topPerformers,
      needsAttention,
      courseEngagement,
      weeklyActivity
    });
  }, []);

  // Load teacher data
  useEffect(() => {
    let isMounted = true;
    
    const loadTeacherData = async () => {
      // Get token directly from localStorage to avoid stale state
      const currentToken = localStorage.getItem('faheem_token');
      
      console.log("TeacherProfile: Starting loadTeacherData");
      console.log("TeacherProfile: Token exists:", !!currentToken);
      
      if (!currentToken) {
        console.log("TeacherProfile: No token found, redirecting to login");
        if (isMounted) navigate('/login');
        return;
      }

      try {
        if (isMounted) setLoading(true);

        // First: Check if user is a teacher (required before other calls)
        console.log("TeacherProfile: Checking if user is teacher...");
        const teacherCheck = await axios.get(`${BACKEND_URL}/api/teacher/check`, {
          params: { access_token: currentToken }
        });
        console.log("TeacherProfile: Teacher check response:", teacherCheck.data);

        if (!isMounted) return;

        if (!teacherCheck.data.is_teacher) {
          console.log("TeacherProfile: User is not a teacher, redirecting to profile");
          toast.error('You do not have teacher access');
          navigate('/profile');
          return;
        }

        setIsTeacher(true);
        setTeacherCourses(teacherCheck.data.courses || []);

        // Second: Load all other data in parallel
        console.log("TeacherProfile: Loading profile, students, submissions...");
        const [profileResponse, studentsResponse, submissionsResponse] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/supabase-auth/profile`, {
            params: { access_token: currentToken }
          }),
          axios.get(`${BACKEND_URL}/api/teacher/students`, {
            params: { access_token: currentToken }
          }),
          axios.get(`${BACKEND_URL}/api/teacher/submissions`, {
            params: { access_token: currentToken }
          })
        ]);

        if (!isMounted) return;

        console.log("TeacherProfile: Data loaded successfully");
        setProfile(profileResponse.data);
        
        const studentsData = studentsResponse.data.students || [];
        setStudents(studentsData);
        
        const submissionsData = submissionsResponse.data.submissions || [];
        setSubmissions(submissionsData);
        
        // Calculate analytics
        calculateAnalytics(studentsData, submissionsData, teacherCheck.data.courses || []);

      } catch (error) {
        if (!isMounted) return;
        
        console.error('TeacherProfile: Error loading teacher data:', error);
        console.error('TeacherProfile: Error response status:', error.response?.status);
        console.error('TeacherProfile: Error response data:', error.response?.data);
        
        if (error.response?.status === 401) {
          console.log("TeacherProfile: Got 401, token may be expired");
          toast.error('Session expired. Please login again.');
          localStorage.removeItem('faheem_token');
          localStorage.removeItem('faheem_auth');
          localStorage.removeItem('faheem_user');
          localStorage.removeItem('faheem_role');
          navigate('/login');
        } else {
          toast.error('Failed to load teacher data: ' + (error.response?.data?.detail || error.message));
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    // Run on mount
    loadTeacherData();
    
    return () => {
      isMounted = false;
    };
  }, [BACKEND_URL, navigate, calculateAnalytics]);

  // Load student progress
  const loadStudentProgress = async (studentId) => {
    const token = localStorage.getItem('faheem_token');
    try {
      setStudentProgress(null); // Clear previous data immediately
      setLoadingProgress(true);
      const response = await axios.get(`${BACKEND_URL}/api/teacher/student/${studentId}/progress`, {
        params: { access_token: token }
      });
      setStudentProgress(response.data);
    } catch (error) {
      console.error('Error loading student progress:', error);
      toast.error('Failed to load student progress');
      setStudentProgress(null);
    } finally {
      setLoadingProgress(false);
    }
  };

  // Load class performance data
  const loadClassPerformance = async () => {
    const token = localStorage.getItem('faheem_token');
    try {
      setLoadingClassPerformance(true);
      const response = await axios.get(`${BACKEND_URL}/api/teacher/class-performance`, {
        params: { access_token: token }
      });
      setClassPerformance(response.data);
    } catch (error) {
      console.error('Error loading class performance:', error);
    } finally {
      setLoadingClassPerformance(false);
    }
  };

  // Load class performance when tab changes
  useEffect(() => {
    const token = localStorage.getItem('faheem_token');
    if (activeTab === 'class-performance' && !classPerformance && token) {
      loadClassPerformance();
    }
  }, [activeTab, classPerformance]);

  // Load student progress when student is selected
  useEffect(() => {
    const token = localStorage.getItem('faheem_token');
    if (selectedStudent && token) {
      loadStudentProgress(selectedStudent.id);
    }
  }, [selectedStudent]);

  // Handle grading
  const handleGrade = async () => {
    if (!gradingSubmission || !gradeValue) {
      toast.error('Please enter a grade');
      return;
    }

    const grade = parseFloat(gradeValue);
    if (isNaN(grade) || grade < 0 || grade > 100) {
      toast.error('Grade must be between 0 and 100');
      return;
    }

    const token = localStorage.getItem('faheem_token');
    try {
      setIsGrading(true);
      await axios.post(`${BACKEND_URL}/api/teacher/grade`, {
        submission_id: gradingSubmission.id,
        grade: grade,
        feedback: feedbackValue || null
      }, {
        params: { access_token: token }
      });

      toast.success('Grade submitted successfully!');
      
      // Update submissions list
      const updatedSubmissions = submissions.map(s => 
        s.id === gradingSubmission.id 
          ? { ...s, grade: grade, feedback: feedbackValue, is_graded: true }
          : s
      );
      setSubmissions(updatedSubmissions);
      
      // Recalculate analytics
      calculateAnalytics(students, updatedSubmissions, teacherCourses);

      setGradingSubmission(null);
      setGradeValue('');
      setFeedbackValue('');
    } catch (error) {
      console.error('Error grading submission:', error);
      toast.error('Failed to submit grade');
    } finally {
      setIsGrading(false);
    }
  };

  // Filter students by search and course
  const filteredStudents = students.filter(student => {
    const matchesSearch = searchQuery === '' || 
      student.username?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      student.email?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCourse = selectedCourse === 'all' || 
      (student.enrolled_courses && student.enrolled_courses.includes(selectedCourse));
    return matchesSearch && matchesCourse;
  });

  // Filter submissions by course
  const filteredSubmissions = selectedCourse === 'all' 
    ? submissions 
    : submissions.filter(s => s.course_id === selectedCourse);

  // Get students for a specific course
  const getStudentsForCourse = (courseId) => {
    return students.filter(s => s.enrolled_courses && s.enrolled_courses.includes(courseId));
  };

  // Chart colors
  const COLORS = ['#22c55e', '#3b82f6', '#f59e0b', '#f97316', '#ef4444'];

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600 font-medium">Loading teacher dashboard...</p>
        </div>
      </div>
    );
  }

  if (!isTeacher) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-6">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6">
            <div className="text-center">
              <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-slate-900 mb-2">Access Denied</h2>
              <p className="text-slate-600 mb-6">You do not have teacher access.</p>
              <Button onClick={() => navigate('/profile')}>
                Go to Profile
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 font-sans">
      {/* Header - Same as Home page */}
      <Header />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Profile Header */}
        <div className="mb-8">
          <Card className="bg-gradient-to-r from-slate-800 via-slate-900 to-slate-800 border-0 text-white overflow-hidden">
            <CardContent className="p-6 sm:p-8">
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
                <Avatar className="w-20 h-20 sm:w-24 sm:h-24 border-4 border-white/20 shadow-xl">
                  <AvatarImage src={profile?.avatar_url} />
                  <AvatarFallback className="bg-white/20 text-white text-2xl font-bold">
                    {profile?.username?.charAt(0).toUpperCase() || 'T'}
                  </AvatarFallback>
                </Avatar>
                
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h1 className="text-2xl sm:text-3xl font-bold">{profile?.username || 'Teacher'}</h1>
                    <Badge className="bg-white/20 text-white border-white/30">
                      <GraduationCap className="w-3 h-3 mr-1" />
                      Instructor
                    </Badge>
                  </div>
                  <p className="text-slate-300 flex items-center gap-2 text-sm">
                    <Mail className="w-4 h-4" />
                    {profile?.email}
                  </p>
                  {profile?.bio && (
                    <p className="text-slate-300 mt-2 text-sm max-w-xl">{profile.bio}</p>
                  )}
                </div>
                
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 w-full sm:w-auto mt-4 sm:mt-0">
                  <div className="bg-white/10 rounded-xl p-3 text-center backdrop-blur-sm">
                    <p className="text-3xl font-bold">{teacherCourses.length}</p>
                    <p className="text-xs text-slate-400">Courses</p>
                  </div>
                  <div className="bg-white/10 rounded-xl p-3 text-center backdrop-blur-sm">
                    <p className="text-3xl font-bold">{analytics.totalStudents}</p>
                    <p className="text-xs text-slate-400">Students</p>
                  </div>
                  <div className="bg-white/10 rounded-xl p-3 text-center backdrop-blur-sm">
                    <p className="text-3xl font-bold">{analytics.pendingGrades}</p>
                    <p className="text-xs text-slate-400">To Grade</p>
                  </div>
                  <div className="bg-white/10 rounded-xl p-3 text-center backdrop-blur-sm">
                    <p className="text-3xl font-bold">{analytics.avgClassPerformance}%</p>
                    <p className="text-xs text-slate-400">Avg Grade</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-white border border-slate-200 p-1 shadow-sm rounded-xl h-auto w-full flex">
            <TabsTrigger value="overview" className="data-[state=active]:bg-slate-900 data-[state=active]:text-white flex-1 text-center justify-center rounded-lg py-2.5 text-sm font-medium transition-all">
              <BarChart3 className="w-4 h-4 mr-1.5" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="courses" className="data-[state=active]:bg-slate-900 data-[state=active]:text-white flex-1 text-center justify-center rounded-lg py-2.5 text-sm font-medium transition-all">
              <BookOpen className="w-4 h-4 mr-1.5" />
              Courses
            </TabsTrigger>
            <TabsTrigger value="students" className="data-[state=active]:bg-slate-900 data-[state=active]:text-white flex-1 text-center justify-center rounded-lg py-2.5 text-sm font-medium transition-all">
              <Users className="w-4 h-4 mr-1.5" />
              Students
            </TabsTrigger>
            <TabsTrigger value="grading" className="data-[state=active]:bg-slate-900 data-[state=active]:text-white flex-1 text-center justify-center rounded-lg py-2.5 text-sm font-medium transition-all">
              <ClipboardCheck className="w-4 h-4 mr-1.5" />
              Grading
              {analytics.pendingGrades > 0 && (
                <Badge className="ml-1.5 bg-amber-500 text-white text-xs">{analytics.pendingGrades}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="student-insights" className="data-[state=active]:bg-slate-900 data-[state=active]:text-white flex-1 text-center justify-center rounded-lg py-2.5 text-sm font-medium transition-all">
              <Brain className="w-4 h-4 mr-1.5" />
              Insights
            </TabsTrigger>
            <TabsTrigger value="class-performance" className="data-[state=active]:bg-slate-900 data-[state=active]:text-white flex-1 text-center justify-center rounded-lg py-2.5 text-sm font-medium transition-all">
              <Target className="w-4 h-4 mr-1.5" />
              Performance
            </TabsTrigger>
            <TabsTrigger value="analytics" className="data-[state=active]:bg-slate-900 data-[state=active]:text-white flex-1 text-center justify-center rounded-lg py-2.5 text-sm font-medium transition-all">
              <TrendingUp className="w-4 h-4 mr-1.5" />
              Analytics
            </TabsTrigger>
            <TabsTrigger value="ai-insights" className="data-[state=active]:bg-slate-900 data-[state=active]:text-white flex-1 text-center justify-center rounded-lg py-2.5 text-sm font-medium transition-all">
              <Brain className="w-4 h-4 mr-1.5" />
              AI Insights
            </TabsTrigger>
            <TabsTrigger value="messages" className="data-[state=active]:bg-slate-900 data-[state=active]:text-white flex-1 text-center justify-center rounded-lg py-2.5 text-sm font-medium transition-all">
              <MessageSquare className="w-4 h-4 mr-1.5" />
              Messages
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            {/* Quick Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card className="bg-white border-slate-200 hover:shadow-md transition-shadow">
                <CardContent className="p-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500 mb-1">Total Students</p>
                      <p className="text-3xl font-bold text-slate-900">{analytics.totalStudents}</p>
                    </div>
                    <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                      <Users className="w-6 h-6 text-blue-600" />
                    </div>
                  </div>
                  <p className="text-xs text-slate-500 mt-2">Across {teacherCourses.length} courses</p>
                </CardContent>
              </Card>
              
              <Card className="bg-white border-slate-200 hover:shadow-md transition-shadow">
                <CardContent className="p-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500 mb-1">Avg Performance</p>
                      <p className="text-3xl font-bold text-slate-900">{analytics.avgClassPerformance}%</p>
                    </div>
                    <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                      <TrendingUp className="w-6 h-6 text-green-600" />
                    </div>
                  </div>
                  <p className="text-xs text-green-600 mt-2">
                    {analytics.avgClassPerformance >= 80 ? '↑ Above target' : analytics.avgClassPerformance >= 70 ? '→ On track' : '↓ Needs attention'}
                  </p>
                </CardContent>
              </Card>
              
              <Card className="bg-white border-slate-200 hover:shadow-md transition-shadow">
                <CardContent className="p-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500 mb-1">Pending Grades</p>
                      <p className="text-3xl font-bold text-amber-600">{analytics.pendingGrades}</p>
                    </div>
                    <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                      <Clock className="w-6 h-6 text-amber-600" />
                    </div>
                  </div>
                  <Button 
                    size="sm" 
                    variant="link" 
                    className="text-xs p-0 h-auto mt-2 text-amber-600"
                    onClick={() => setActiveTab('grading')}
                  >
                    Review now →
                  </Button>
                </CardContent>
              </Card>
              
              <Card className="bg-white border-slate-200 hover:shadow-md transition-shadow">
                <CardContent className="p-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500 mb-1">Graded</p>
                      <p className="text-3xl font-bold text-slate-900">{analytics.gradedSubmissions}</p>
                    </div>
                    <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                      <CheckCircle2 className="w-6 h-6 text-purple-600" />
                    </div>
                  </div>
                  <p className="text-xs text-slate-500 mt-2">Total submissions graded</p>
                </CardContent>
              </Card>
            </div>

            {/* Charts Row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Grade Distribution */}
              <Card className="bg-white border-slate-200">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <PieChart className="w-5 h-5 text-blue-600" />
                    Grade Distribution
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {analytics.gradeDistribution.some(d => d.value > 0) ? (
                    <ResponsiveContainer width="100%" height={220}>
                      <RechartsPie>
                        <Pie
                          data={analytics.gradeDistribution}
                          cx="50%"
                          cy="50%"
                          innerRadius={50}
                          outerRadius={80}
                          paddingAngle={2}
                          dataKey="value"
                          label={({ name, value }) => value > 0 ? `${name}: ${value}` : ''}
                        >
                          {analytics.gradeDistribution.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </RechartsPie>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-[220px] flex items-center justify-center text-slate-500">
                      No graded submissions yet
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Weekly Activity */}
              <Card className="bg-white border-slate-200">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Activity className="w-5 h-5 text-purple-600" />
                    Weekly Activity
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={analytics.weeklyActivity}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="day" tick={{ fontSize: 12 }} stroke="#94a3b8" />
                      <YAxis tick={{ fontSize: 12 }} stroke="#94a3b8" />
                      <Tooltip />
                      <Bar dataKey="submissions" fill="#3b82f6" name="Submissions" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="graded" fill="#22c55e" name="Graded" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </div>

            {/* Top Performers & Needs Attention */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Top Performers */}
              <Card className="bg-white border-slate-200">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Medal className="w-5 h-5 text-amber-500" />
                    Top Performers
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {analytics.topPerformers.length > 0 ? (
                    <div className="space-y-3">
                      {analytics.topPerformers.map((student, index) => (
                        <div 
                          key={student.id} 
                          className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors cursor-pointer"
                          onClick={() => {
                            setSelectedStudent(students.find(s => s.id === student.id));
                            loadStudentProgress(student.id);
                          }}
                        >
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm ${
                            index === 0 ? 'bg-amber-500' : index === 1 ? 'bg-slate-400' : index === 2 ? 'bg-amber-700' : 'bg-slate-300'
                          }`}>
                            {index + 1}
                          </div>
                          <div className="flex-1">
                            <p className="font-medium text-slate-900">{student.name}</p>
                            <p className="text-xs text-slate-500">{student.count} submissions</p>
                          </div>
                          <div className="text-right">
                            <p className="text-xl font-bold text-green-600">{student.avg}%</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-slate-500">
                      <Medal className="w-12 h-12 mx-auto mb-3 opacity-30" />
                      <p>No graded submissions yet</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Needs Attention */}
              <Card className="bg-white border-slate-200">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-red-500" />
                    Needs Attention
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {analytics.needsAttention.length > 0 ? (
                    <div className="space-y-3">
                      {analytics.needsAttention.map((student) => (
                        <div 
                          key={student.id} 
                          className="flex items-center gap-3 p-3 bg-red-50 rounded-lg hover:bg-red-100 transition-colors cursor-pointer"
                          onClick={() => {
                            const fullStudent = students.find(s => s.id === student.id);
                            if (fullStudent) {
                              setSelectedStudent(fullStudent);
                              loadStudentProgress(student.id);
                            }
                          }}
                        >
                          <div className="w-8 h-8 rounded-full bg-red-200 flex items-center justify-center">
                            <AlertCircle className="w-4 h-4 text-red-600" />
                          </div>
                          <div className="flex-1">
                            <p className="font-medium text-slate-900">{student.name}</p>
                            <p className="text-xs text-red-600">
                              {student.noSubmissions ? 'No submissions' : `${student.count} submissions`}
                            </p>
                          </div>
                          <div className="text-right">
                            {!student.noSubmissions && (
                              <p className="text-xl font-bold text-red-600">{student.avg}%</p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-slate-500">
                      <CheckCircle2 className="w-12 h-12 mx-auto mb-3 text-green-500 opacity-50" />
                      <p>All students performing well!</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Courses Tab */}
          <TabsContent value="courses" className="space-y-6">
            <div className="grid grid-cols-1 gap-4">
              {teacherCourses.length === 0 ? (
                <Card className="bg-white border-slate-200">
                  <CardContent className="py-12 text-center">
                    <BookOpen className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-slate-700 mb-2">No Courses Assigned</h3>
                    <p className="text-slate-500">You have not been assigned to any courses yet.</p>
                  </CardContent>
                </Card>
              ) : (
                teacherCourses.map((course) => {
                  const courseStudents = getStudentsForCourse(course.id);
                  const courseSubs = submissions.filter(s => s.course_id === course.id);
                  const pendingSubs = courseSubs.filter(s => !s.is_graded);
                  const isExpanded = expandedCourse === course.id;
                  
                  return (
                    <Card key={course.id} className="bg-white border-slate-200 overflow-hidden">
                      <div 
                        className="p-5 cursor-pointer hover:bg-slate-50 transition-colors"
                        onClick={() => setExpandedCourse(isExpanded ? null : course.id)}
                      >
                        <div className="flex items-start gap-4">
                          <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center flex-shrink-0">
                            <BookOpen className="w-7 h-7 text-white" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-3">
                              <div>
                                <h3 className="text-lg font-bold text-slate-900">{course.title}</h3>
                                <p className="text-sm text-slate-500 mt-0.5">{course.type || 'Course'} • ID: {course.id}</p>
                              </div>
                              <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                            </div>
                            <div className="flex items-center gap-4 mt-3">
                              <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                                <Users className="w-3 h-3 mr-1" />
                                {courseStudents.length} Students
                              </Badge>
                              <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">
                                <FileText className="w-3 h-3 mr-1" />
                                {courseSubs.length} Submissions
                              </Badge>
                              {pendingSubs.length > 0 && (
                                <Badge className="bg-amber-100 text-amber-700 border-amber-200">
                                  <Clock className="w-3 h-3 mr-1" />
                                  {pendingSubs.length} Pending
                                </Badge>
                              )}
                            </div>
                          </div>
                          <Link to={`/course/${course.id}`}>
                            <Button variant="outline" size="sm" onClick={(e) => e.stopPropagation()}>
                              <ExternalLink className="w-4 h-4 mr-1" />
                              View
                            </Button>
                          </Link>
                        </div>
                      </div>
                      
                      {/* Expanded Content */}
                      {isExpanded && (
                        <div className="border-t border-slate-200 bg-slate-50 p-5">
                          <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                            <Users className="w-4 h-4 text-blue-600" />
                            Enrolled Students ({courseStudents.length})
                          </h4>
                          {courseStudents.length === 0 ? (
                            <p className="text-slate-500 text-sm">No students enrolled yet.</p>
                          ) : (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                              {courseStudents.map((student) => {
                                const studentSubs = courseSubs.filter(s => s.student_id === student.id);
                                const avgGrade = studentSubs.filter(s => s.is_graded).reduce((acc, s) => acc + s.grade, 0) / (studentSubs.filter(s => s.is_graded).length || 1);
                                
                                return (
                                  <div 
                                    key={student.id}
                                    className="flex items-center gap-3 p-3 bg-white rounded-lg border border-slate-200 hover:border-blue-300 cursor-pointer transition-colors"
                                    onClick={() => {
                                      setSelectedStudent(student);
                                      loadStudentProgress(student.id);
                                    }}
                                  >
                                    <Avatar className="w-10 h-10">
                                      <AvatarImage src={student.avatar_url} />
                                      <AvatarFallback className="bg-blue-100 text-blue-600 font-semibold">
                                        {student.username?.charAt(0).toUpperCase() || 'S'}
                                      </AvatarFallback>
                                    </Avatar>
                                    <div className="flex-1 min-w-0">
                                      <p className="font-medium text-slate-900 truncate">{student.username || 'Student'}</p>
                                      <p className="text-xs text-slate-500">{studentSubs.length} submissions</p>
                                    </div>
                                    {studentSubs.some(s => s.is_graded) && (
                                      <Badge className={`${avgGrade >= 80 ? 'bg-green-100 text-green-700' : avgGrade >= 60 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>
                                        {Math.round(avgGrade)}%
                                      </Badge>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      )}
                    </Card>
                  );
                })
              )}
            </div>
          </TabsContent>

          {/* Students Tab */}
          <TabsContent value="students" className="space-y-6">
            {/* Filters */}
            <Card className="bg-white border-slate-200">
              <CardContent className="p-4">
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="flex-1">
                    <div className="relative">
                      <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
                      <Input
                        placeholder="Search students..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-9"
                      />
                    </div>
                  </div>
                  <Select value={selectedCourse} onValueChange={setSelectedCourse}>
                    <SelectTrigger className="w-full sm:w-[200px]">
                      <SelectValue placeholder="Filter by course" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Courses</SelectItem>
                      {teacherCourses.map(course => (
                        <SelectItem key={course.id} value={course.id}>{course.title}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            {/* Students List */}
            {filteredStudents.length === 0 ? (
              <Card className="bg-white border-slate-200">
                <CardContent className="py-12 text-center">
                  <Users className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-700 mb-2">No Students Found</h3>
                  <p className="text-slate-500">Try adjusting your search or filters.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredStudents.map((student) => {
                  const studentSubs = submissions.filter(s => s.student_id === student.id);
                  const gradedSubs = studentSubs.filter(s => s.is_graded);
                  const avgGrade = gradedSubs.length > 0 
                    ? Math.round(gradedSubs.reduce((acc, s) => acc + s.grade, 0) / gradedSubs.length)
                    : null;
                  
                  return (
                    <Card 
                      key={student.id}
                      className="bg-white border-slate-200 hover:border-blue-300 hover:shadow-md transition-all cursor-pointer"
                      onClick={() => {
                        setSelectedStudent(student);
                        loadStudentProgress(student.id);
                      }}
                    >
                      <CardContent className="p-5">
                        <div className="flex items-start gap-4">
                          <Avatar className="w-14 h-14">
                            <AvatarImage src={student.avatar_url} />
                            <AvatarFallback className="bg-gradient-to-br from-blue-500 to-purple-600 text-white font-bold text-lg">
                              {student.username?.charAt(0).toUpperCase() || 'S'}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1 min-w-0">
                            <h3 className="font-semibold text-slate-900 truncate">{student.username || 'Student'}</h3>
                            <p className="text-sm text-slate-500 truncate">{student.email}</p>
                            <div className="flex items-center gap-2 mt-2">
                              <Badge variant="outline" className="text-xs">
                                {student.total_enrolled || student.enrolled_courses?.length || 0} courses
                              </Badge>
                              <Badge variant="outline" className="text-xs">
                                {studentSubs.length} submissions
                              </Badge>
                            </div>
                          </div>
                        </div>
                        
                        {avgGrade !== null && (
                          <div className="mt-4 pt-4 border-t border-slate-100">
                            <div className="flex items-center justify-between">
                              <span className="text-sm text-slate-600">Average Grade</span>
                              <span className={`text-xl font-bold ${
                                avgGrade >= 90 ? 'text-green-600' :
                                avgGrade >= 80 ? 'text-blue-600' :
                                avgGrade >= 70 ? 'text-amber-600' :
                                'text-red-600'
                              }`}>{avgGrade}%</span>
                            </div>
                            <Progress 
                              value={avgGrade} 
                              className={`h-2 mt-2 ${
                                avgGrade >= 90 ? '[&>div]:bg-green-500' :
                                avgGrade >= 80 ? '[&>div]:bg-blue-500' :
                                avgGrade >= 70 ? '[&>div]:bg-amber-500' :
                                '[&>div]:bg-red-500'
                              }`}
                            />
                          </div>
                        )}
                        
                        {/* Message Button */}
                        <div className="mt-4 pt-4 border-t border-slate-100 flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            className="flex-1"
                            onClick={(e) => {
                              e.stopPropagation();
                              setActiveTab('messages');
                            }}
                          >
                            <MessageSquare className="w-4 h-4 mr-1" />
                            Message
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* Grading Tab */}
          <TabsContent value="grading" className="space-y-6">
            {/* Filter */}
            <Card className="bg-white border-slate-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-4">
                  <Select value={selectedCourse} onValueChange={setSelectedCourse}>
                    <SelectTrigger className="w-[200px]">
                      <SelectValue placeholder="Filter by course" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Courses</SelectItem>
                      {teacherCourses.map(course => (
                        <SelectItem key={course.id} value={course.id}>{course.title}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="flex-1" />
                  <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                    {filteredSubmissions.filter(s => !s.is_graded).length} Pending
                  </Badge>
                  <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                    {filteredSubmissions.filter(s => s.is_graded).length} Graded
                  </Badge>
                </div>
              </CardContent>
            </Card>

            {/* Submissions List */}
            {filteredSubmissions.length === 0 ? (
              <Card className="bg-white border-slate-200">
                <CardContent className="py-12 text-center">
                  <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-700 mb-2">No Submissions Yet</h3>
                  <p className="text-slate-500">Student submissions will appear here.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {/* Pending Submissions */}
                {filteredSubmissions.filter(s => !s.is_graded).length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2">
                      <Clock className="w-5 h-5 text-amber-500" />
                      Pending Review
                    </h3>
                    <div className="space-y-3">
                      {filteredSubmissions.filter(s => !s.is_graded).map((submission) => (
                        <Card key={submission.id} className="bg-amber-50 border-amber-200">
                          <CardContent className="p-4">
                            <div className="flex items-center gap-4">
                              <Avatar className="w-12 h-12">
                                <AvatarFallback className="bg-amber-200 text-amber-700">
                                  {submission.student_name?.charAt(0).toUpperCase() || 'S'}
                                </AvatarFallback>
                              </Avatar>
                              <div className="flex-1 min-w-0">
                                <h4 className="font-semibold text-slate-900">{submission.student_name}</h4>
                                <p className="text-sm text-slate-600">{submission.course_title}</p>
                                <p className="text-xs text-slate-500">
                                  Submitted: {new Date(submission.created_at).toLocaleDateString()} • {submission.file_name}
                                </p>
                              </div>
                              <div className="flex items-center gap-2">
                                {submission.file_url && (
                                  <Button variant="outline" size="sm" onClick={() => window.open(submission.file_url, '_blank')}>
                                    <Eye className="w-4 h-4 mr-1" />
                                    View
                                  </Button>
                                )}
                                <Button size="sm" onClick={() => setGradingSubmission(submission)} className="bg-blue-600 hover:bg-blue-700">
                                  <Star className="w-4 h-4 mr-1" />
                                  Grade
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}

                {/* Graded Submissions */}
                {filteredSubmissions.filter(s => s.is_graded).length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2 mt-6">
                      <CheckCircle2 className="w-5 h-5 text-green-500" />
                      Graded
                    </h3>
                    <div className="space-y-3">
                      {filteredSubmissions.filter(s => s.is_graded).map((submission) => (
                        <Card key={submission.id} className="bg-green-50 border-green-200">
                          <CardContent className="p-4">
                            <div className="flex items-center gap-4">
                              <Avatar className="w-12 h-12">
                                <AvatarFallback className="bg-green-200 text-green-700">
                                  {submission.student_name?.charAt(0).toUpperCase() || 'S'}
                                </AvatarFallback>
                              </Avatar>
                              <div className="flex-1 min-w-0">
                                <h4 className="font-semibold text-slate-900">{submission.student_name}</h4>
                                <p className="text-sm text-slate-600">{submission.course_title}</p>
                                {submission.feedback && (
                                  <p className="text-xs text-slate-500 mt-1 line-clamp-1">
                                    Feedback: {submission.feedback}
                                  </p>
                                )}
                              </div>
                              <div className="text-right">
                                <p className="text-2xl font-bold text-green-600">{submission.grade}</p>
                                <p className="text-xs text-green-500">/ 100</p>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </TabsContent>

          {/* Student Insights Tab - Monitor individual students' learning patterns */}
          <TabsContent value="student-insights" className="space-y-6">
            <Card className="bg-gradient-to-r from-slate-800 via-slate-900 to-slate-800 border-0 text-white">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center">
                    <Brain className="w-6 h-6 text-blue-400" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold">Student Learning Insights</h2>
                    <p className="text-slate-400">Monitor individual student progress and learning patterns</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Student Selector */}
            <Card className="bg-white border-slate-200">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <User className="w-5 h-5 text-slate-600" />
                  Select a Student
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex gap-4 mb-4">
                  <div className="flex-1">
                    <Input
                      placeholder="Search students..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="max-w-sm"
                    />
                  </div>
                  <Select value={selectedCourse} onValueChange={setSelectedCourse}>
                    <SelectTrigger className="w-48">
                      <SelectValue placeholder="Filter by course" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Courses</SelectItem>
                      {teacherCourses.map(course => (
                        <SelectItem key={course.id} value={course.id}>{course.title}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 max-h-64 overflow-y-auto">
                  {students
                    .filter(s => s.username?.toLowerCase().includes(searchQuery.toLowerCase()) || 
                                 s.email?.toLowerCase().includes(searchQuery.toLowerCase()))
                    .map(student => (
                      <div
                        key={student.id}
                        onClick={() => setSelectedStudent(student)}
                        className={`p-3 rounded-lg border cursor-pointer transition-all ${
                          selectedStudent?.id === student.id 
                            ? 'border-slate-900 bg-slate-50 shadow-md' 
                            : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <Avatar className="w-10 h-10">
                            <AvatarImage src={student.avatar_url} />
                            <AvatarFallback className="bg-slate-200 text-slate-700">
                              {student.username?.charAt(0).toUpperCase() || 'S'}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-slate-800 truncate">{student.username}</p>
                            <p className="text-xs text-slate-500 truncate">{student.email}</p>
                          </div>
                          {selectedStudent?.id === student.id && (
                            <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" />
                          )}
                        </div>
                      </div>
                    ))}
                </div>
              </CardContent>
            </Card>

            {/* Selected Student Details */}
            {selectedStudent && (
              <div className="space-y-6">
                {loadingProgress ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="text-center">
                      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-slate-800 mx-auto mb-3"></div>
                      <p className="text-slate-500">Loading student data...</p>
                    </div>
                  </div>
                ) : studentProgress ? (
                  <>
                    {/* Student Overview */}
                    <Card className="bg-white border-slate-200">
                      <CardContent className="p-6">
                        <div className="flex items-center gap-6">
                          <Avatar className="w-20 h-20">
                            <AvatarImage src={selectedStudent.avatar_url} />
                            <AvatarFallback className="bg-slate-800 text-white text-2xl">
                              {selectedStudent.username?.charAt(0).toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1">
                            <h3 className="text-xl font-semibold text-slate-800">{selectedStudent.username}</h3>
                            <p className="text-sm text-slate-500">{selectedStudent.email}</p>
                            <div className="flex gap-4 mt-3">
                              <div className="text-center">
                                <p className="text-2xl font-bold text-blue-600">{studentProgress.total_quizzes_taken || 0}</p>
                                <p className="text-xs text-slate-500">Quizzes Taken</p>
                              </div>
                              <div className="text-center">
                                <p className="text-2xl font-bold text-green-600">{studentProgress.avg_quiz_score || 0}%</p>
                                <p className="text-xs text-slate-500">Avg Score</p>
                              </div>
                              <div className="text-center">
                                <p className="text-2xl font-bold text-purple-600">{studentProgress.courses?.length || 0}</p>
                                <p className="text-xs text-slate-500">Courses</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Quiz Performance by Course */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      {studentProgress.courses?.map((course) => (
                        <Card key={course.course_id} className="bg-white border-slate-200">
                          <CardHeader className="pb-3">
                            <CardTitle className="flex items-center justify-between">
                              <span className="text-base font-semibold">{course.course_title}</span>
                              <Badge className={course.quiz_summary?.avg_score >= 70 ? 'bg-green-100 text-green-700' : course.quiz_summary?.avg_score >= 50 ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'}>
                                {course.quiz_summary?.avg_score || 0}% avg
                              </Badge>
                            </CardTitle>
                            <CardDescription>
                              {course.quiz_summary?.total_attempts || 0} quizzes • {course.quiz_summary?.topics_with_quizzes || 0} topics covered
                            </CardDescription>
                          </CardHeader>
                          <CardContent>
                            {course.topic_quiz_scores && course.topic_quiz_scores.length > 0 ? (
                              <div className="space-y-3">
                                {course.topic_quiz_scores.map((topic) => (
                                  <div key={topic.topic_id} className="p-3 bg-slate-50 rounded-lg">
                                    <div className="flex items-center justify-between mb-2">
                                      <p className="font-medium text-slate-800 text-sm">{topic.topic_name}</p>
                                      <div className="flex items-center gap-2">
                                        <span className="text-xs text-slate-500">{topic.attempts} attempts</span>
                                        <Badge className={topic.best_score >= 80 ? 'bg-green-600 text-white' : topic.best_score >= 60 ? 'bg-blue-600 text-white' : 'bg-amber-600 text-white'}>
                                          Best: {topic.best_score}%
                                        </Badge>
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <div className="flex-1 h-2 bg-slate-200 rounded-full overflow-hidden">
                                        <div 
                                          className={`h-full rounded-full ${topic.avg_score >= 70 ? 'bg-green-500' : topic.avg_score >= 50 ? 'bg-amber-500' : 'bg-red-500'}`}
                                          style={{ width: `${topic.avg_score}%` }}
                                        />
                                      </div>
                                      <span className="text-sm font-medium text-slate-700 w-12 text-right">{topic.avg_score}%</span>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-center text-slate-500 py-4">No quiz data for this course</p>
                            )}
                          </CardContent>
                        </Card>
                      ))}
                    </div>

                    {/* Recent Quiz Attempts */}
                    <Card className="bg-white border-slate-200">
                      <CardHeader className="pb-3">
                        <CardTitle className="flex items-center gap-2 text-lg">
                          <ClipboardCheck className="w-5 h-5 text-blue-600" />
                          Recent Quiz Attempts
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2 max-h-64 overflow-y-auto">
                          {studentProgress.courses?.flatMap(c => c.quizzes || [])
                            .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                            .slice(0, 10)
                            .map((quiz, idx) => (
                              <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                                <div>
                                  <p className="font-medium text-slate-800 text-sm">{quiz.topic_name}</p>
                                  <p className="text-xs text-slate-500">
                                    {new Date(quiz.created_at).toLocaleDateString()} • {quiz.correct_count}/{quiz.total_questions} correct
                                  </p>
                                </div>
                                <Badge className={quiz.percentage >= 80 ? 'bg-green-600 text-white' : quiz.percentage >= 60 ? 'bg-blue-600 text-white' : quiz.percentage >= 40 ? 'bg-amber-600 text-white' : 'bg-red-600 text-white'}>
                                  {quiz.percentage}%
                                </Badge>
                              </div>
                            ))}
                          {studentProgress.courses?.flatMap(c => c.quizzes || []).length === 0 && (
                            <p className="text-center text-slate-500 py-4">No quiz attempts yet</p>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  </>
                ) : (
                  <Card className="bg-white border-slate-200">
                    <CardContent className="py-12 text-center">
                      <AlertTriangle className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                      <p className="text-slate-500">No progress data available for this student</p>
                      <Button 
                        variant="outline" 
                        className="mt-4"
                        onClick={() => loadStudentProgress(selectedStudent.id)}
                      >
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Retry Loading
                      </Button>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}
          </TabsContent>

          {/* Class Performance Tab - Overview of all students */}
          <TabsContent value="class-performance" className="space-y-6">
            {loadingClassPerformance ? (
              <div className="flex items-center justify-center py-20">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-800 mx-auto mb-4"></div>
                  <p className="text-slate-500">Loading class performance data...</p>
                </div>
              </div>
            ) : classPerformance ? (
              <>
                {/* Header Stats */}
                <Card className="bg-gradient-to-r from-slate-800 via-slate-900 to-slate-800 border-0 text-white">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between flex-wrap gap-4">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center">
                          <Target className="w-6 h-6 text-green-400" />
                        </div>
                        <div>
                          <h2 className="text-xl font-bold">Class Performance Dashboard</h2>
                          <p className="text-slate-400">Quiz-based student performance analytics</p>
                        </div>
                      </div>
                      <div className="flex gap-4">
                        <div className="bg-white/10 rounded-xl p-3 text-center min-w-[100px]">
                          <p className="text-2xl font-bold">{classPerformance.total_students}</p>
                          <p className="text-xs text-slate-400">Students</p>
                        </div>
                        <div className="bg-white/10 rounded-xl p-3 text-center min-w-[100px]">
                          <p className="text-2xl font-bold">{classPerformance.avg_quiz_score}%</p>
                          <p className="text-xs text-slate-400">Avg Score</p>
                        </div>
                        <div className="bg-white/10 rounded-xl p-3 text-center min-w-[100px]">
                          <p className="text-2xl font-bold">{classPerformance.total_quizzes_taken}</p>
                          <p className="text-xs text-slate-400">Quizzes Taken</p>
                        </div>
                        <Button 
                          variant="outline" 
                          size="sm"
                          className="bg-white/10 border-white/20 text-white hover:bg-white/20"
                          onClick={loadClassPerformance}
                        >
                          <RefreshCw className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Top Performers & Needs Attention */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Top Performers */}
                  <Card className="bg-white border-slate-200">
                    <CardHeader className="pb-3">
                      <CardTitle className="flex items-center gap-2 text-lg">
                        <Trophy className="w-5 h-5 text-yellow-500" />
                        Top Performers
                      </CardTitle>
                      <CardDescription>Students with highest quiz scores</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {classPerformance.top_performers?.map((student, idx) => (
                          <div key={student.id} className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-white ${
                              idx === 0 ? 'bg-yellow-500' : idx === 1 ? 'bg-slate-400' : idx === 2 ? 'bg-amber-600' : 'bg-slate-500'
                            }`}>
                              {idx + 1}
                            </div>
                            <Avatar className="w-10 h-10">
                              <AvatarImage src={student.avatar_url} />
                              <AvatarFallback className="bg-green-200 text-green-800">
                                {student.username?.charAt(0).toUpperCase() || 'S'}
                              </AvatarFallback>
                            </Avatar>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-slate-800 truncate">{student.username}</p>
                              <p className="text-xs text-slate-500">{student.submissions} quizzes • {student.courses} courses</p>
                            </div>
                            <Badge className="bg-green-600 text-white">{student.avgGrade}%</Badge>
                          </div>
                        ))}
                        {(!classPerformance.top_performers || classPerformance.top_performers.length === 0) && (
                          <p className="text-center text-slate-500 py-4">No quiz data yet</p>
                        )}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Students Needing Support */}
                  <Card className="bg-white border-slate-200">
                    <CardHeader className="pb-3">
                      <CardTitle className="flex items-center gap-2 text-lg">
                        <AlertTriangle className="w-5 h-5 text-amber-500" />
                        Students Needing Support
                      </CardTitle>
                      <CardDescription>Students who may need additional help</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {classPerformance.needs_attention?.map((student) => (
                          <div key={student.id} className="flex items-center gap-3 p-3 bg-amber-50 rounded-lg">
                            <Avatar className="w-10 h-10">
                              <AvatarImage src={student.avatar_url} />
                              <AvatarFallback className="bg-amber-200 text-amber-800">
                                {student.username?.charAt(0).toUpperCase() || 'S'}
                              </AvatarFallback>
                            </Avatar>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-slate-800 truncate">{student.username}</p>
                              <p className="text-xs text-slate-500">{student.reason}</p>
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge className="bg-amber-600 text-white">{student.avgGrade}%</Badge>
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => {
                                  const fullStudent = students.find(s => s.id === student.id) || student;
                                  setSelectedStudent(fullStudent);
                                  setActiveTab('student-insights');
                                }}
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        ))}
                        {(!classPerformance.needs_attention || classPerformance.needs_attention.length === 0) && (
                          <div className="text-center py-4">
                            <CheckCircle2 className="w-8 h-8 text-green-500 mx-auto mb-2" />
                            <p className="text-slate-500">All students are performing well!</p>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Course Performance */}
                <Card className="bg-white border-slate-200">
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <BookOpen className="w-5 h-5 text-blue-600" />
                      Performance by Course
                    </CardTitle>
                    <CardDescription>Quiz scores across your courses</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {classPerformance.course_performance?.map((course) => (
                        <div key={course.course_id} className="p-4 bg-slate-50 rounded-xl border border-slate-100">
                          <h4 className="font-semibold text-slate-800 mb-2 truncate">{course.course_title}</h4>
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm text-slate-600">Average Score</span>
                            <Badge className={course.avg_score >= 70 ? 'bg-green-100 text-green-700' : course.avg_score >= 50 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}>
                              {course.avg_score}%
                            </Badge>
                          </div>
                          <div className="h-2 bg-slate-200 rounded-full overflow-hidden mb-3">
                            <div 
                              className={`h-full rounded-full ${course.avg_score >= 70 ? 'bg-green-500' : course.avg_score >= 50 ? 'bg-amber-500' : 'bg-red-500'}`}
                              style={{ width: `${course.avg_score}%` }}
                            />
                          </div>
                          <div className="flex justify-between text-xs text-slate-500">
                            <span>{course.students} students</span>
                            <span>{course.total_quizzes} quizzes</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Grade Distribution & Topic Performance */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Grade Distribution */}
                  <Card className="bg-white border-slate-200">
                    <CardHeader className="pb-3">
                      <CardTitle className="flex items-center gap-2 text-lg">
                        <PieChart className="w-5 h-5 text-purple-600" />
                        Grade Distribution
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {classPerformance.grade_distribution?.map((grade) => {
                          const total = classPerformance.total_quizzes_taken || 1;
                          const percentage = Math.round((grade.count / total) * 100);
                          return (
                            <div key={grade.name} className="flex items-center gap-3">
                              <div className="w-4 h-4 rounded" style={{ backgroundColor: grade.color }}></div>
                              <div className="flex-1">
                                <div className="flex justify-between text-sm mb-1">
                                  <span className="text-slate-700">{grade.name}</span>
                                  <span className="font-medium">{grade.count} ({percentage}%)</span>
                                </div>
                                <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                                  <div 
                                    className="h-full rounded-full"
                                    style={{ width: `${percentage}%`, backgroundColor: grade.color }}
                                  />
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Topic Performance */}
                  <Card className="bg-white border-slate-200">
                    <CardHeader className="pb-3">
                      <CardTitle className="flex items-center gap-2 text-lg">
                        <BarChart3 className="w-5 h-5 text-indigo-600" />
                        Topic Performance
                      </CardTitle>
                      <CardDescription>Average scores by topic</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3 max-h-64 overflow-y-auto">
                        {classPerformance.topic_performance?.slice(0, 10).map((topic) => (
                          <div key={topic.topic_id} className="flex items-center gap-3">
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-slate-800 truncate">{topic.topic_name}</p>
                              <p className="text-xs text-slate-500">{topic.attempts} attempts • {topic.students} students</p>
                            </div>
                            <Badge className={topic.avg_score >= 70 ? 'bg-green-100 text-green-700' : topic.avg_score >= 50 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}>
                              {topic.avg_score}%
                            </Badge>
                          </div>
                        ))}
                        {(!classPerformance.topic_performance || classPerformance.topic_performance.length === 0) && (
                          <p className="text-center text-slate-500 py-4">No topic data available</p>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </>
            ) : (
              <Card className="bg-white border-slate-200">
                <CardContent className="py-16 text-center">
                  <Target className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-700 mb-2">No Performance Data</h3>
                  <p className="text-slate-500 max-w-md mx-auto mb-4">
                    Class performance data will appear here once students start taking quizzes.
                  </p>
                  <Button onClick={loadClassPerformance}>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Refresh Data
                  </Button>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics" className="space-y-6">
            {/* Course Engagement Chart */}
            <Card className="bg-white border-slate-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-blue-600" />
                  Course Engagement
                </CardTitle>
                <CardDescription>Students and submissions per course</CardDescription>
              </CardHeader>
              <CardContent>
                {analytics.courseEngagement.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={analytics.courseEngagement} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis type="number" tick={{ fontSize: 12 }} stroke="#94a3b8" />
                      <YAxis dataKey="name" type="category" width={150} tick={{ fontSize: 11 }} stroke="#94a3b8" />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="students" fill="#3b82f6" name="Students" radius={[0, 4, 4, 0]} />
                      <Bar dataKey="submissions" fill="#8b5cf6" name="Submissions" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[300px] flex items-center justify-center text-slate-500">
                    No data available yet
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Summary Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white border-0">
                <CardContent className="p-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-blue-100 text-sm">Total Students</p>
                      <p className="text-3xl font-bold mt-1">{analytics.totalStudents}</p>
                    </div>
                    <Users className="w-10 h-10 text-blue-200" />
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white border-0">
                <CardContent className="p-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-green-100 text-sm">Avg Performance</p>
                      <p className="text-3xl font-bold mt-1">{analytics.avgClassPerformance}%</p>
                    </div>
                    <TrendingUp className="w-10 h-10 text-green-200" />
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white border-0">
                <CardContent className="p-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-purple-100 text-sm">Total Graded</p>
                      <p className="text-3xl font-bold mt-1">{analytics.gradedSubmissions}</p>
                    </div>
                    <CheckCircle2 className="w-10 h-10 text-purple-200" />
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white border-0">
                <CardContent className="p-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-amber-100 text-sm">Courses Teaching</p>
                      <p className="text-3xl font-bold mt-1">{teacherCourses.length}</p>
                    </div>
                    <BookOpen className="w-10 h-10 text-amber-200" />
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Messages Tab */}
          <TabsContent value="messages" className="space-y-6">
            <MessagingPanel userType="teacher" students={students} />
          </TabsContent>

          {/* AI Interaction Insights Tab */}
          <TabsContent value="ai-insights" className="space-y-6">
            <AIInteractionInsights 
              students={students} 
              teacherCourses={teacherCourses}
            />
          </TabsContent>
        </Tabs>
      </main>

      {/* Student Progress Dialog */}
      <Dialog open={!!selectedStudent} onOpenChange={(open) => {
        if (!open) {
          setSelectedStudent(null);
          setStudentProgress(null);
        }
      }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto bg-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <Avatar className="w-12 h-12">
                <AvatarImage src={studentProgress?.student?.avatar_url || selectedStudent?.avatar_url} />
                <AvatarFallback className="bg-blue-100 text-blue-600 font-bold">
                  {selectedStudent?.username?.charAt(0).toUpperCase() || 'S'}
                </AvatarFallback>
              </Avatar>
              <div>
                <p className="text-lg font-bold">{selectedStudent?.username || 'Student'}</p>
                <p className="text-sm font-normal text-slate-500">{selectedStudent?.email}</p>
              </div>
            </DialogTitle>
            <DialogDescription>
              {loadingProgress ? 'Loading progress...' : `Overall Progress: ${studentProgress?.overall_progress || 0}%`}
            </DialogDescription>
          </DialogHeader>

          {loadingProgress ? (
            <div className="flex flex-col items-center justify-center py-12 gap-3">
              <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
              <p className="text-sm text-slate-500">Loading student progress...</p>
            </div>
          ) : studentProgress ? (
            <div className="space-y-4">
              {/* Circular Progress Summary */}
              <div className="grid grid-cols-4 gap-3 p-4 bg-slate-50 rounded-xl">
                {/* Overall Progress */}
                <div className="flex flex-col items-center">
                  <div className="relative w-16 h-16">
                    <svg className="w-16 h-16 transform -rotate-90">
                      <circle
                        cx="32"
                        cy="32"
                        r="28"
                        stroke="#e2e8f0"
                        strokeWidth="5"
                        fill="none"
                      />
                      <circle
                        cx="32"
                        cy="32"
                        r="28"
                        stroke="#3b82f6"
                        strokeWidth="5"
                        fill="none"
                        strokeLinecap="round"
                        strokeDasharray={`${2 * Math.PI * 28}`}
                        strokeDashoffset={`${2 * Math.PI * 28 * (1 - (studentProgress.overall_progress || 0) / 100)}`}
                        className="transition-all duration-500"
                      />
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-sm font-bold text-blue-600">{studentProgress.overall_progress || 0}%</span>
                    </div>
                  </div>
                  <p className="text-xs text-slate-600 mt-1 font-medium">Overall</p>
                </div>

                {/* Topics Progress */}
                {(() => {
                  const totalTopics = studentProgress.courses?.reduce((acc, c) => acc + (c.total_topics || 0), 0) || 0;
                  const completedTopics = studentProgress.courses?.reduce((acc, c) => acc + (c.topics_completed || 0), 0) || 0;
                  const topicsPercent = totalTopics > 0 ? Math.round((completedTopics / totalTopics) * 100) : 0;
                  return (
                    <div className="flex flex-col items-center">
                      <div className="relative w-16 h-16">
                        <svg className="w-16 h-16 transform -rotate-90">
                          <circle
                            cx="32"
                            cy="32"
                            r="28"
                            stroke="#e2e8f0"
                            strokeWidth="5"
                            fill="none"
                          />
                          <circle
                            cx="32"
                            cy="32"
                            r="28"
                            stroke="#22c55e"
                            strokeWidth="5"
                            fill="none"
                            strokeLinecap="round"
                            strokeDasharray={`${2 * Math.PI * 28}`}
                            strokeDashoffset={`${2 * Math.PI * 28 * (1 - topicsPercent / 100)}`}
                            className="transition-all duration-500"
                          />
                        </svg>
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className="text-sm font-bold text-green-600">{topicsPercent}%</span>
                        </div>
                      </div>
                      <p className="text-xs text-slate-600 mt-1 font-medium">Topics</p>
                      <p className="text-xs text-slate-400">{completedTopics}/{totalTopics}</p>
                    </div>
                  );
                })()}

                {/* Assignments Progress */}
                {(() => {
                  const totalAssignments = studentProgress.courses?.reduce((acc, c) => acc + (c.total_assignments || 0), 0) || 0;
                  const completedAssignments = studentProgress.courses?.reduce((acc, c) => acc + (c.assignments_completed || 0), 0) || 0;
                  const assignmentsPercent = totalAssignments > 0 ? Math.round((completedAssignments / totalAssignments) * 100) : 0;
                  return (
                    <div className="flex flex-col items-center">
                      <div className="relative w-16 h-16">
                        <svg className="w-16 h-16 transform -rotate-90">
                          <circle
                            cx="32"
                            cy="32"
                            r="28"
                            stroke="#e2e8f0"
                            strokeWidth="5"
                            fill="none"
                          />
                          <circle
                            cx="32"
                            cy="32"
                            r="28"
                            stroke="#8b5cf6"
                            strokeWidth="5"
                            fill="none"
                            strokeLinecap="round"
                            strokeDasharray={`${2 * Math.PI * 28}`}
                            strokeDashoffset={`${2 * Math.PI * 28 * (1 - assignmentsPercent / 100)}`}
                            className="transition-all duration-500"
                          />
                        </svg>
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className="text-sm font-bold text-purple-600">{assignmentsPercent}%</span>
                        </div>
                      </div>
                      <p className="text-xs text-slate-600 mt-1 font-medium">Assignments</p>
                      <p className="text-xs text-slate-400">{completedAssignments}/{totalAssignments}</p>
                    </div>
                  );
                })()}

                {/* Quiz Performance */}
                {(() => {
                  const totalQuizzes = studentProgress.courses?.reduce((acc, c) => acc + (c.quiz_summary?.total_attempts || 0), 0) || 0;
                  const avgQuizScore = totalQuizzes > 0 
                    ? Math.round(studentProgress.courses?.reduce((acc, c) => {
                        const attempts = c.quiz_summary?.total_attempts || 0;
                        const avg = c.quiz_summary?.avg_score || 0;
                        return acc + (attempts * avg);
                      }, 0) / totalQuizzes)
                    : 0;
                  return (
                    <div className="flex flex-col items-center">
                      <div className="relative w-16 h-16">
                        <svg className="w-16 h-16 transform -rotate-90">
                          <circle
                            cx="32"
                            cy="32"
                            r="28"
                            stroke="#e2e8f0"
                            strokeWidth="5"
                            fill="none"
                          />
                          <circle
                            cx="32"
                            cy="32"
                            r="28"
                            stroke="#f59e0b"
                            strokeWidth="5"
                            fill="none"
                            strokeLinecap="round"
                            strokeDasharray={`${2 * Math.PI * 28}`}
                            strokeDashoffset={`${2 * Math.PI * 28 * (1 - avgQuizScore / 100)}`}
                            className="transition-all duration-500"
                          />
                        </svg>
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className="text-sm font-bold text-amber-600">{avgQuizScore}%</span>
                        </div>
                      </div>
                      <p className="text-xs text-slate-600 mt-1 font-medium">Quizzes</p>
                      <p className="text-xs text-slate-400">{totalQuizzes} taken</p>
                    </div>
                  );
                })()}
              </div>

              {/* Course Details */}
              <div className="space-y-3">
                {studentProgress.courses?.map((course) => (
                  <Card key={course.course_id} className="bg-slate-50 border-slate-200">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-3">
                        <p className="font-semibold text-slate-900">{course.course_title}</p>
                        <Badge variant="outline" className="bg-white">{course.overall_progress}%</Badge>
                      </div>
                      <Progress value={course.overall_progress} className="h-2 mb-3" />
                      
                      {/* Course Stats with Mini Circular Progress */}
                      <div className="grid grid-cols-3 gap-3">
                        <div className="flex items-center gap-2 p-2 bg-white rounded-lg border border-slate-100">
                          <div className="relative w-10 h-10">
                            <svg className="w-10 h-10 transform -rotate-90">
                              <circle cx="20" cy="20" r="16" stroke="#e2e8f0" strokeWidth="3" fill="none" />
                              <circle
                                cx="20" cy="20" r="16"
                                stroke="#22c55e"
                                strokeWidth="3"
                                fill="none"
                                strokeLinecap="round"
                                strokeDasharray={`${2 * Math.PI * 16}`}
                                strokeDashoffset={`${2 * Math.PI * 16 * (1 - (course.topic_progress_pct || 0) / 100)}`}
                              />
                            </svg>
                            <div className="absolute inset-0 flex items-center justify-center">
                              <span className="text-xs font-bold text-green-600">{course.topic_progress_pct || 0}%</span>
                            </div>
                          </div>
                          <div>
                            <p className="text-xs text-slate-500">Topics</p>
                            <p className="text-sm font-semibold text-slate-900">{course.topics_completed}/{course.total_topics}</p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2 p-2 bg-white rounded-lg border border-slate-100">
                          <div className="relative w-10 h-10">
                            <svg className="w-10 h-10 transform -rotate-90">
                              <circle cx="20" cy="20" r="16" stroke="#e2e8f0" strokeWidth="3" fill="none" />
                              <circle
                                cx="20" cy="20" r="16"
                                stroke="#8b5cf6"
                                strokeWidth="3"
                                fill="none"
                                strokeLinecap="round"
                                strokeDasharray={`${2 * Math.PI * 16}`}
                                strokeDashoffset={`${2 * Math.PI * 16 * (1 - (course.assignment_progress_pct || 0) / 100)}`}
                              />
                            </svg>
                            <div className="absolute inset-0 flex items-center justify-center">
                              <span className="text-xs font-bold text-purple-600">{course.assignment_progress_pct || 0}%</span>
                            </div>
                          </div>
                          <div>
                            <p className="text-xs text-slate-500">Assignments</p>
                            <p className="text-sm font-semibold text-slate-900">{course.assignments_completed}/{course.total_assignments}</p>
                          </div>
                        </div>

                        {/* Quiz Stats */}
                        <div className="flex items-center gap-2 p-2 bg-white rounded-lg border border-slate-100">
                          <div className="relative w-10 h-10">
                            <svg className="w-10 h-10 transform -rotate-90">
                              <circle cx="20" cy="20" r="16" stroke="#e2e8f0" strokeWidth="3" fill="none" />
                              <circle
                                cx="20" cy="20" r="16"
                                stroke="#f59e0b"
                                strokeWidth="3"
                                fill="none"
                                strokeLinecap="round"
                                strokeDasharray={`${2 * Math.PI * 16}`}
                                strokeDashoffset={`${2 * Math.PI * 16 * (1 - (course.quiz_summary?.avg_score || 0) / 100)}`}
                              />
                            </svg>
                            <div className="absolute inset-0 flex items-center justify-center">
                              <span className="text-xs font-bold text-amber-600">{course.quiz_summary?.avg_score || 0}%</span>
                            </div>
                          </div>
                          <div>
                            <p className="text-xs text-slate-500">Quizzes</p>
                            <p className="text-sm font-semibold text-slate-900">{course.quiz_summary?.total_attempts || 0} taken</p>
                          </div>
                        </div>
                      </div>

                      {/* Quiz Scores by Topic */}
                      {course.topic_quiz_scores && course.topic_quiz_scores.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-slate-200">
                          <p className="text-xs text-slate-500 mb-2 font-medium flex items-center gap-1">
                            <ClipboardCheck className="w-3 h-3" />
                            Quiz Scores by Topic:
                          </p>
                          <div className="space-y-2">
                            {course.topic_quiz_scores.map((topic, idx) => (
                              <div key={idx} className="bg-white p-2 rounded border border-slate-100">
                                <div className="flex items-center justify-between mb-1">
                                  <span className="text-sm font-medium text-slate-800 truncate flex-1">{topic.topic_name}</span>
                                  <div className="flex items-center gap-2">
                                    <span className="text-xs text-slate-500">{topic.attempts} attempts</span>
                                    <Badge className={topic.best_score >= 80 ? 'bg-green-100 text-green-700' : topic.best_score >= 60 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}>
                                      Best: {topic.best_score}%
                                    </Badge>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  <div className="flex-1 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                                    <div 
                                      className={`h-full rounded-full ${topic.avg_score >= 70 ? 'bg-green-500' : topic.avg_score >= 50 ? 'bg-amber-500' : 'bg-red-500'}`}
                                      style={{ width: `${topic.avg_score}%` }}
                                    />
                                  </div>
                                  <span className="text-xs font-medium text-slate-600 w-10 text-right">Avg: {topic.avg_score}%</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Recent Quiz Attempts */}
                      {course.quizzes && course.quizzes.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-slate-200">
                          <p className="text-xs text-slate-500 mb-2 font-medium flex items-center gap-1">
                            <FileText className="w-3 h-3" />
                            Recent Quiz Attempts:
                          </p>
                          <div className="space-y-1.5 max-h-32 overflow-y-auto">
                            {course.quizzes.slice(0, 5).map((quiz, idx) => (
                              <div key={idx} className="flex items-center justify-between text-sm bg-white p-2 rounded border border-slate-100">
                                <div className="flex-1 min-w-0">
                                  <span className="truncate block text-slate-700">{quiz.topic_name}</span>
                                  <span className="text-xs text-slate-400">{new Date(quiz.created_at).toLocaleDateString()}</span>
                                </div>
                                <div className="flex items-center gap-2 ml-2">
                                  <span className="text-xs text-slate-500">{quiz.correct_count}/{quiz.total_questions}</span>
                                  <Badge className={quiz.percentage >= 80 ? 'bg-green-100 text-green-700' : quiz.percentage >= 60 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}>
                                    {quiz.percentage}%
                                  </Badge>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {course.submissions?.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-slate-200">
                          <p className="text-xs text-slate-500 mb-2 font-medium flex items-center gap-1">
                            <FileUp className="w-3 h-3" />
                            Submissions:
                          </p>
                          <div className="space-y-2">
                            {course.submissions.map((sub, idx) => (
                              <div key={idx} className="flex items-center justify-between text-sm bg-white p-2 rounded border border-slate-100">
                                <span className="truncate flex-1">{sub.file_name}</span>
                                {sub.grade !== null ? (
                                  <Badge className="bg-green-100 text-green-700 ml-2">{sub.grade}/100</Badge>
                                ) : (
                                  <Badge className="bg-amber-100 text-amber-700 ml-2">Pending</Badge>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Peer Comparison */}
              {studentProgress.peer_comparison && (
                <Card className="bg-gradient-to-r from-slate-800 to-slate-900 text-white">
                  <CardContent className="p-4">
                    <p className="text-sm font-semibold mb-3 flex items-center gap-2">
                      <Users className="w-4 h-4" />
                      Peer Comparison
                    </p>
                    <div className="grid grid-cols-3 gap-3">
                      <div className="bg-white/10 rounded-lg p-2 text-center">
                        <p className="text-lg font-bold">{studentProgress.peer_comparison.percentile || 50}%</p>
                        <p className="text-xs text-slate-300">Percentile</p>
                      </div>
                      <div className="bg-white/10 rounded-lg p-2 text-center">
                        <p className="text-lg font-bold">{studentProgress.peer_comparison.class_avg || 0}%</p>
                        <p className="text-xs text-slate-300">Class Avg</p>
                      </div>
                      <div className="bg-white/10 rounded-lg p-2 text-center">
                        <p className="text-lg font-bold">{studentProgress.peer_comparison.rank || '-'}</p>
                        <p className="text-xs text-slate-300">Rank</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-500">
              <p>No progress data available</p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Grading Dialog */}
      <Dialog open={!!gradingSubmission} onOpenChange={() => setGradingSubmission(null)}>
        <DialogContent className="sm:max-w-lg bg-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Award className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <span>Grade Submission</span>
                <p className="text-sm font-normal text-slate-500 mt-0.5">
                  {gradingSubmission?.student_name}
                </p>
              </div>
            </DialogTitle>
            <DialogDescription>
              {gradingSubmission?.course_title}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div>
              <Label htmlFor="grade" className="text-sm font-medium">Grade (0-100)</Label>
              <Input
                id="grade"
                type="number"
                min="0"
                max="100"
                value={gradeValue}
                onChange={(e) => setGradeValue(e.target.value)}
                placeholder="Enter grade..."
                className="mt-1.5 text-lg font-semibold"
              />
            </div>

            <div>
              <Label htmlFor="feedback" className="text-sm font-medium">Feedback (Optional)</Label>
              <Textarea
                id="feedback"
                value={feedbackValue}
                onChange={(e) => setFeedbackValue(e.target.value)}
                placeholder="Enter feedback for the student..."
                rows={4}
                className="mt-1.5"
              />
            </div>

            {gradingSubmission?.file_url && (
              <Button 
                variant="outline" 
                className="w-full" 
                onClick={() => window.open(gradingSubmission.file_url, '_blank')}
              >
                <Eye className="w-4 h-4 mr-2" />
                View Submission
              </Button>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setGradingSubmission(null)}>
              Cancel
            </Button>
            <Button 
              onClick={handleGrade} 
              disabled={isGrading || !gradeValue}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {isGrading ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Submit Grade
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
